import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
} from "@mui/material";
import React, { useState } from "react";
import NavBar from "../../components/navbar";
import { useNavigate } from "react-router-dom";
import { LoadExternalInventoryByEan } from "../../../../domain/usecases/load-external-inventory-by-ean";
import { LoadInventoryByEan } from "../../../../domain/usecases/load-inventory-by-ean";
import { useErrorHandler } from "../../hooks";
import { Table } from "./table";
import { LoadProductListByManyCodes } from "../../../../domain/usecases/load-product-list-by-many-codes";

type Props = {
  loadExternalInventory: LoadExternalInventoryByEan;
  loadInventory: LoadInventoryByEan;
  loadProductsByManyCodes: LoadProductListByManyCodes;
};

const branchOptions = ["02", "06", "07", "71", "08", "09", "10"];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CheckFullInventory = ({
  loadExternalInventory,
  loadInventory,
  loadProductsByManyCodes,
}: Props) => {
  const [branches, setBranches] = React.useState<string[]>(["71"]);
  const [rows, setRows] = React.useState<
    Array<{
      id: string;
      code: string;
      description?: string;
      fiscalAmount: number;
      amount: number;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleError = useErrorHandler((e) => {
    alert(e.message);
  });

  const handleChange = (event: SelectChangeEvent<typeof branches>) => {
    const {
      target: { value },
    } = event;
    setBranches(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const compare = async () => {
    try {
      const warehouseString = localStorage.getItem("currentWarehouse");
      const warehouse = warehouseString
        ? (JSON.parse(warehouseString) as {
            id: number;
            name: string;
          } | null)
        : undefined;
      const inventory = await loadInventory.load({
        warehouseId: warehouse!.id,
      });
      const externalInventory = await loadExternalInventory.load([], branches);
      const rows: Array<{
        id: string;
        code: string;
        description?: string;
        fiscalAmount: number;
        amount: number;
        difference: number;
      }> = [];
      const groupedInventory = Object.values(
        inventory.reduce(
          (
            acc: {
              [code: string]: {
                code: string;
                amount: number;
              };
            },
            item
          ) => {
            const amount = item.amount;
            const code = item.item!.code;
            if (acc[code]) {
              acc[code].amount += amount;
            } else {
              acc[code] = {
                code,
                amount: item.amount,
              };
            }
            return acc;
          },
          {}
        )
      );
      const groupedExternalInventory = Object.values(
        externalInventory.reduce(
          (
            acc: {
              [code: string]: {
                code: string;
                description: string;
                amount: number;
              };
            },
            item
          ) => {
            const amount = item.amount;
            const code = item.eanCode;
            if (acc[code]) {
              acc[code].amount += amount;
            } else {
              acc[code] = {
                code,
                description: item.description!,
                amount: item.amount,
              };
            }
            return acc;
          },
          {}
        )
      );
      groupedExternalInventory.forEach((inventory) =>
        rows.push({
          id: inventory.code,
          code: inventory.code,
          description: inventory.description,
          fiscalAmount: Number(inventory.amount.toFixed(4)),
          amount: 0,
          difference: 0,
        })
      );
      groupedInventory.forEach((inventory) => {
        const match = rows.find((item) => item.code === inventory.code);
        if (match) {
          match.amount = Number(inventory.amount.toFixed(4));
        } else {
          rows.push({
            id: inventory.code,
            code: inventory.code,
            fiscalAmount: 0,
            amount: Number(inventory.amount.toFixed(4)),
            difference: 0,
          });
        }
      });
      const codes = rows.map((row) => {
        if (!row.description) return row.code;
      }) as string[];

      const productList = await loadProductsByManyCodes.load(codes);
      productList.forEach((product) => {
        const match = rows.find((row) => row.code === product.eanCode);
        if (match) {
          match.description = product.description;
        }
      });
      const data = rows.map((element) => ({
        ...element,
        difference: Number((element.amount - element.fiscalAmount).toFixed(2)),
      }));
      setRows(data);
    } catch (error: any) {
      handleError(error);
    }
  };
  return (
    <>
      <NavBar
        title="Relatórios"
        onBack={() => {
          navigate(-1);
        }}
      />
      <Container
        sx={{
          my: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack direction="row" spacing={4}>
          <FormControl sx={{ m: 1, width: 150 }}>
            <InputLabel id="demo-multiple-checkbox-label">Filial</InputLabel>
            <Select
              labelId="demo-multiple-checkbox-label"
              label="Filial"
              placeholder="Filial"
              id="demo-multiple-checkbox"
              multiple
              value={branches}
              onChange={handleChange}
              input={<OutlinedInput label="Tag" />}
              renderValue={(selected) => selected.join(", ")}
              MenuProps={MenuProps}
            >
              {branchOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={branches.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Container>
      <Divider>
        <Button
          variant="contained"
          size="large"
          startIcon={
            isLoading && <CircularProgress color="secondary" size={20} />
          }
          onClick={compare}
        >
          Comparar
        </Button>
      </Divider>
      <Container sx={{ my: "50px" }}>
        <Table data={rows} />
      </Container>
    </>
  );
};

export default CheckFullInventory;
